import ky, { KyInstance } from "ky";
import posthog from "posthog-js";

import { useCallback } from "react";

import { reportApiFailure } from "@/lib/api-failure-telemetry";
import {
  ClientRateLimitError,
  RATE_LIMIT_BYPASS_HEADER,
  checkAndRecord,
  reportRateLimitTrip,
} from "@/lib/client-rate-limiter";
import { getAPIBaseURL, isServedOverNgrok } from "@/lib/get-base-url";
import { markErrorHandled } from "@/lib/posthog-error-dedup";
import { getTimezone } from "@/lib/timezone";

import {
  dashboardTimeout,
  routeToClinicReports,
} from "@/api/analytics/clinic-report-scope";
import { authService } from "@/services/auth-service";

// Store for dynamic headers that can be updated at runtime
const dynamicHeaders = new Map<string, string>();

// Endpoints whose request body carries a secret — gift card codes, the
// web-chat graduation's opaque session token (a live credential for the guest
// chat until it graduates/expires), the staff device PIN, and the Microsoft ID
// token behind verified-email sign-in. The beforeError telemetry hook must never
// ship their raw body to PostHog.
const SENSITIVE_BODY_URL_RE =
  /\/gift-cards\/(?:search|balance|code\/validate)(?:[/?#]|$)|\/patient\/web-chat\/graduate(?:[/?#]|$)|\/user\/me\/pin(?:\/verify)?(?:[/?#]|$)|\/auth\/microsoft\/verified-email(?:[/?#]|$)/;

/**
 * Set a dynamic header that will be included in all API requests
 * @param key - Header name (e.g., "MERCHANT_ACCOUNT", "TENANT")
 * @param value - Header value
 */
export function setDynamicHeader(key: string, value: string) {
  dynamicHeaders.set(key, value);
}

/**
 * Remove a dynamic header
 * @param key - Header name to remove
 */
export function removeDynamicHeader(key: string) {
  dynamicHeaders.delete(key);
}

/**
 * Clear all dynamic headers
 */
export function clearDynamicHeaders() {
  dynamicHeaders.clear();
}

export function useDynamicHeaders() {
  const setHeader = useCallback((key: string, value: string) => {
    setDynamicHeader(key, value);
  }, []);

  const removeHeader = useCallback((key: string) => {
    removeDynamicHeader(key);
  }, []);

  const clearHeaders = useCallback(() => {
    clearDynamicHeaders();
  }, []);

  return { setHeader, removeHeader, clearHeaders };
}

/** Whether a request names a TENANT other than the one in the URL, as cross-clinic reads do. */
export function targetsOtherTenant(request: Request): boolean {
  if (typeof window === "undefined") return false;
  const target = request.headers.get("TENANT");
  const current = window.location.pathname.split("/")[1];
  return Boolean(
    target && current && current !== "login" && target !== current,
  );
}

export const kyInstance: KyInstance = ky.create({
  retry: 2,
  prefixUrl: getAPIBaseURL(),
  timeout: 20000,
  hooks: {
    beforeRequest: [
      async (request) => {
        // Client-side rate limiter — short-circuit runaway pages before they
        // hit the network. Per-path (150/10s) catches single-endpoint spam
        // (e.g. cursor-pagination loops); blanket per-user (300/10s) catches
        // multi-path / path-iteration fan-outs.
        //
        // The limiter's own Slack self-alert sets RATE_LIMIT_BYPASS_HEADER on
        // its POST /errors/log so a blanket trip can't block the very alert
        // that's notifying us about it. The bypass requires BOTH the header
        // AND the /errors/log pathname so the header can't be used to skip
        // the limiter on arbitrary routes if it's ever set elsewhere by
        // mistake. App-level /errors/log calls (error boundary,
        // useErrorLogging) don't set the header and remain rate-limited.
        const url = new URL(request.url);
        const isLimiterSelfAlert =
          url.pathname === "/errors/log" &&
          request.headers.get(RATE_LIMIT_BYPASS_HEADER) === "1";
        if (!isLimiterSelfAlert) {
          const pathKey = `${request.method.toUpperCase()}:${url.pathname}`;
          const decision = checkAndRecord(pathKey);
          if (!decision.allowed) {
            const err = new ClientRateLimitError(
              decision.scope,
              pathKey,
              decision.retryAfterMs,
            );
            reportRateLimitTrip(decision.scope, pathKey, url.pathname);
            // Note: not calling markErrorHandled — ky's beforeError hook
            // only fires for HTTPError (response failures), not for errors
            // thrown synchronously here. There's no capture to suppress.
            throw err;
          }
        }

        // Set tenant header from URL, unless the caller named one
        if (typeof window !== "undefined" && !request.headers.has("TENANT")) {
          const pathParts = window.location.pathname.split("/");
          if (pathParts.length > 1) {
            const tenant = pathParts[1];
            if (tenant !== "login") {
              request.headers.set("TENANT", tenant);
            }
          }
        }

        // ngrok's free domains answer a browser's first request with a warning
        // page unless asked not to.
        if (isServedOverNgrok()) {
          request.headers.set("ngrok-skip-browser-warning", "1");
        }

        // Set all dynamic headers
        dynamicHeaders.forEach((value, key) => {
          request.headers.set(key, value);
        });

        const token = await authService.getToken();
        if (token) {
          request.headers.set("Authorization", `Bearer ${token}`);
        }
      },
      routeToClinicReports,
    ],
    beforeError: [
      async (error) => {
        try {
          let body: string | undefined;
          try {
            body = await error.request.clone().text();
          } catch {
            // body already consumed or not readable
          }
          // Redact bodies that carry bearer secrets so codes never reach telemetry.
          if (body && SENSITIVE_BODY_URL_RE.test(error.request.url)) {
            body = "[redacted]";
          }
          posthog.captureException(error, {
            $ky_error: true,
            $request_url: error.request.url,
            $request_method: error.request.method,
            $request_body: body,
            $response_status: error.response?.status,
          });
          markErrorHandled(error);
        } catch {
          // never let logging break the request flow
        }
        return error;
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        // Handle 403 Forbidden - could be IP blocking
        // Backend already skips IP blocking for public routes, so we only need to check
        // if this is an authenticated request (has Authorization header)
        if (
          response.status === 403 &&
          request.headers.get("Authorization") &&
          !targetsOtherTenant(request)
        ) {
          try {
            // Clone response to read body without consuming it
            const clonedResponse = response.clone();
            const errorData = await clonedResponse.json();

            // Check if this is an IP blocking error (backend only returns these for non-public routes)
            // Be specific - check for IP blocking keywords in the error message
            const errorDetail = errorData?.detail;
            if (
              errorDetail &&
              typeof errorDetail === "string" &&
              (errorDetail.toLowerCase().includes("ip address") ||
                errorDetail.toLowerCase().includes("ip blocking") ||
                errorDetail.toLowerCase().includes("allowed list"))
            ) {
              // This is an IP blocking error - show toast and logout
              await authService.handleIPBlockingError({
                response: { status: 403, json: async () => errorData },
              });
              if (!authService.isLoggingOut) {
                await authService.logout(true); // Suppress default toast
                // Don't redirect here - let the login page handle staying on login
              }
              return response;
            }
          } catch {
            // If parsing fails, fall through to default handling
          }
        }

        // Only 401 (token invalid/expired) should trigger a token refresh + retry.
        // 403 means the user is authenticated but not authorized — refreshing the
        // token won't change permissions, and retrying non-idempotent requests
        // (PUT/POST) can cause duplicate side effects on the server.
        if (response.status === 401) {
          // Retry token refresh up to 2 times before giving up. The first
          // attempt runs immediately — authService.getToken already awaits
          // whenReady, so a leading sleep would just penalize every
          // expired-token request. The 500ms backoff is only for the
          // genuine retry after a failed first refresh.
          for (let attempt = 0; attempt < 2; attempt++) {
            if (attempt > 0) {
              await new Promise((r) => setTimeout(r, 500));
            }
            let newToken: string | null = null;
            try {
              newToken = await authService.getToken(true);
            } catch {
              newToken = null;
            }
            if (newToken) {
              request.headers.set("Authorization", `Bearer ${newToken}`);
              return ky(request);
            }
          }
          // All retries failed - logout
          if (!authService.isLoggingOut) {
            await authService.logout(true); // Suppress toast - auth errors shouldn't show "signed out" message
          }
        }
      },
    ],
  },
});

export async function requestData<T>(
  endpoint: string,
  method: "get" | "post" | "put" | "delete" | "patch" = "get",
  options?: any,
  catchError: boolean = true,
  timeout?: number,
): Promise<T | null> {
  if (options?.searchParams) {
    options.searchParams = cleanSearchParams(options.searchParams);
  }

  // Apply timeout if provided
  timeout ??= options?.timeout ?? dashboardTimeout(endpoint);
  if (timeout) {
    options = { ...options, timeout };
  }

  const startedAt = Date.now();

  try {
    const response = await kyInstance[method](endpoint, options);

    // Handle empty responses (e.g., 204 No Content for DELETE requests)
    if (
      response.status === 204 ||
      response.headers.get("content-length") === "0"
    ) {
      return null as T | null;
    }

    const text = await response.text();
    if (!text || text.trim() === "") {
      return null as T | null;
    }

    return JSON.parse(text) as T | null;
  } catch (error: any) {
    // Report before the AbortError early-return and before the catchError
    // branch — ky's beforeError hook only fires for HTTPError, so network
    // failures and timeouts have no other trace anywhere in the stack.
    reportApiFailure(error, method, endpoint, startedAt);

    if (error.name === "AbortError") {
      return null;
    }

    if (catchError) {
      console.error("Error fetching data:", error);
      return null;
    }
    throw error;
  }
}

export function cleanSearchParams(params: any): any {
  if (params instanceof URLSearchParams) return params;

  const cleanedParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === "" || value == null) {
      return; // skip empty values
    }

    if (Array.isArray(value)) {
      // Preserve every position (including null/empty) so range filters keep
      // their [lower, upper] semantics. Nulls become empty strings so the
      // backend can distinguish "no bound" from "bound = 0".
      value.forEach((item) => {
        cleanedParams.append(
          key,
          item != null && item !== "" ? String(item) : "",
        );
      });
    } else if (
      typeof value === "object" &&
      value !== null &&
      (value as object).constructor === Object
    ) {
      // JSON-encode structured objects so they survive URL serialization.
      cleanedParams.append(key, JSON.stringify(value));
    } else {
      cleanedParams.append(key, String(value));
    }
  });

  return cleanedParams;
}

/**
 * Sets the zone a report reads its days in. Endpoints differ on the name
 * (`tz` or `timezone`); keep whichever the backend expects.
 */
export function withTimezoneParam(
  params: URLSearchParams,
  key: "tz" | "timezone" = "tz",
  timeZone: string = getTimezone(),
): URLSearchParams {
  params.set(key, timeZone);
  return params;
}
